import _ from "lodash";
import React from "react";
import { saveAs } from "file-saver";
import axios from "axios";
import { PICKER_DEFAULT_IMAGES, DEFAULT_THUMB_IMAGE } from "store/static";
import { getRethumbUrl } from "./GetRethumbUrl";
import { stringToPascalCase } from "Utils/StringHelpers";
import { generateRandomIdV2 } from "Utils/GenerateRandomId";
import { getGoogleScopeOfUser } from "services";
import {
  colors,
  POST_MEDIA_TYPES,
  GOOGLE_DEVELOPER_KEY,
  MISC_FILE_EXTENSION_MIMETYPE_MAPPING,
  GOOGLE_DRIVE_SCOPE,
} from "Constants";
import mime from "mime-types";
import { ENTITY_LOCKED_STATE } from "Constants/stringConstants";
import {
  WorkbookColored,
  LinkColored,
  FilenotfoundColored,
  FolderColored,
  ImageColored,
  NoteColored,
  PostOneOutlined,
  PdfColored,
  SheetsColored,
  SlidesColored,
  PageSquareColored,
  VideoColored,
  AudioColored,
  TableOutlined,
  ZipFileColored,
  RubricOutlined,
  SinglePointRubricOutlined,
  ChecklistOutlined,
  GoalsOutlined,
  ProgressReportOutlined,
  ScoreBasedAssessmentOutlined,
  CommentTextOutlined,
  WorksheetClipboardOutlined,
  HamburgerOutlined,
  CodeOutlined,
  LinkOutlined,
  PdfOutlined,
  PageSquareOutlined,
  SlidesOutlined,
  SheetsOutlined,
  FileNotFoundOutlined,
  ZipFileOutlined,
  ImageSquareOutlined,
  VideoOutlined,
  VoiceWaveOutlined,
  WorkbookOutlined,
  NoteOutlined,
  FolderOutlined,
  CommentOutlined,
} from "@toddle-edu/ds-icons";

import { allowURLWithCredentials } from "./SecureUrlHelpers";
import { getLTIName } from "LTIComponent/modules/Utils";

export const ATTACHMENT_WARNINGS = {
  GOOGLE_TEACHER_ACCOUNT_NOT_LINKED:
    "toastMsgs:google_teacher_account_not_linked",
  GOOGLE_TEACHER_ACCOUNT_NOT_WORKING:
    "toastMsgs:google_teacher_account_not_working",
  STUDENT_EMAIL_NOT_CONNECTED: "toastMsgs:student_gmail_not_connected",
  STUDENT_MICROSOFT_ACCOUNT_NOT_CONNECTED:
    "toastMsgs:student_microsoft_account_not_connected",
  GOOGLE_NOT_ABLE_TO_ASSIGN_PERMISSION:
    "toastMsgs:google_not_able_to_assign_permission",
  GOOGLE_NOT_ABLE_TO_COPY: "toastMsgs:google_not_able_to_copy",
  ATTACHMENT_WITHOUT_PERMISSION: "toastMsgs:attachments_without_permissions",
  GOOGLE_ACCOUNT_NOT_LINKED: "toastMsgs:google_account_not_linked",
  GOOGLE_ACCOUNT_NOT_WORKING: "toastMsgs:google_account_not_linked",
  MICROSOFT_ACCOUNT_NOT_LINKED: "toastMsgs:microsoft_account_not_linked",
  MICROSOFT_TEACHER_ACCOUNT_NOT_LINKED:
    "toastMsgs:microsoft_teacher_account_connect_issue",
  MICROSOFT_ACCOUNT_NOT_WORKING: "toastMsgs:microsoft_account_not_working",
  MICROSOFT_TEACHER_ACCOUNT_NOT_WORKING:
    "toastMsgs:microsoft_teacher_account_connect_issue",
  MICROSOFT_FILE_DELETED_OR_ACCOUNT_CHANGED:
    "toastMsgs:microsoft_file_deleted_or_account_changed",
  MICROSOFT_FILE_DELETED_OR_TEACHER_ACCOUNT_CHANGED:
    "toastMsgs:microsoft_file_deleted_or_teacher_account_changed",
  MICROSOFT_FILE_DELETED_OR_TEMPLATE_OWNER_ACCOUNT_CHANGED:
    "toastMsgs:microsoft_file_deleted_or_template_owner_account_changed",
  DRIVE_TEMPLATE_OWNER_DISCONNECTED:
    "toastMsgs:drive_template_owner_disconnected",
  STUDENT_EMAIL_NOT_VALID_CONTACT_SUPPORT: "toastMsgs:student_email_not_valid",
};

const TEMPLATE_OPTIONS = [
  { label: "common:drive_student_copy", value: "copy" },
  { label: "common:all_students_can_edit_file", value: "edit" },
];

/**
 * Extracted from a api in google documentation for export formats for each
 * type of google document. It's important to notice that here, only one
 * format is chosen for a given document.
 *
 * Link: https://developers.google.com/drive/api/v3/reference/about/get
 */
const GOOGLE_EXPORT_FORMATS = {
  "application/vnd.google-apps.document": [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  "application/vnd.google-apps.spreadsheet": [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ],
  "application/vnd.google-apps.presentation": [
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ],
  "application/vnd.google-apps.jam": ["application/pdf"],
  "application/vnd.google-apps.script": [
    "application/vnd.google-apps.script+json",
  ],
  "application/vnd.google-apps.form": ["application/zip"],
  "application/vnd.google-apps.drawing": ["image/png"],
  "application/vnd.google-apps.site": ["text/plain"],
};

/**
 * This function takes attachment object type & mimetype of file
 * and suggest which type would be better for "share as file"
 *
 *
 * @param {String} type - attachment type
 * @param {String} mimeType - mimeType
 *
 * @returns {Boolean} return true if supported as file
 */

export const isExportTypePreferredAsFile = ({ type, mimeType }) => {
  if (
    _.includes(["IMAGE", "VIDEO", "AUDIO"], _.toUpper(type)) ||
    _.includes(["application/pdf"], _.toLower(mimeType))
  ) {
    return true;
  }
  return false;
};

/**
 * This function takes file and access token and returns an array
 * of exportable link objects.
 *
 * @param {Object} param0
 * @param {Object} param0.file - Chosen file object from google picker
 * @param {String} param0.accessToken - Google access token
 *
 * @returns {Array} exportLinkObjects - Array of exportable link objects
 */
export const getExportLinks = async ({
  file,
  accessToken,
  serviceType = "GOOGLE",
  userType = "",
  userPreferenceSetting = {},
}) => {
  const baseUrl = _.isEqual(serviceType, "MICROSOFT")
    ? file.url
    : `https://www.googleapis.com/drive/v3/files/${file.id}`;
  const { text: type } = getFileType(file.mimeType) || {};
  const isTeacherEnd = !_.isEmpty(userType) && _.isEqual(userType, "staff");
  const isStudentEnd = !isTeacherEnd && _.isEqual(userType, "student");

  const fileTypeUserPreference = _.toUpper(
    _.get(userPreferenceSetting, "fileExportable", "")
  );
  const linkTypeUserPreference = _.toUpper(
    _.get(userPreferenceSetting, "fileNonExportable", "")
  );
  const fileMimeType = _.get(file, "mimeType");
  const isFileExportable = isExportTypePreferredAsFile({
    type,
    mimeType: fileMimeType,
  });
  let isShareAsFileFirst = false;

  const userSettingPreference = isFileExportable
    ? fileTypeUserPreference
    : linkTypeUserPreference;
  const shouldUseUserPreference =
    isTeacherEnd && type && fileMimeType && !_.isEmpty(userSettingPreference);

  if (
    shouldUseUserPreference &&
    _.includes(["FILE", "LINK"], userSettingPreference)
  ) {
    if (_.isEqual(userSettingPreference, "FILE")) {
      isShareAsFileFirst = true;
    } else {
      isShareAsFileFirst = false;
    }
  } else if (isFileExportable || isStudentEnd) {
    isShareAsFileFirst = true;
  }

  const linkObj = {
    label: "common:drive_share_as_link",
    subText: "common:share_as_link_subtext",
    value: "LINK",
    url: file.url,
    mimeType: file.mimeType,
  };
  // Link as a default option
  const exportLinkObjects = [];
  if (!isShareAsFileFirst) {
    exportLinkObjects.push(linkObj);
  }

  if (serviceType === "GOOGLE") {
    try {
      // Fetch export formats valid for google document
      // TODO: Add caching on network calls
      const response = await axios.get(
        `https://www.googleapis.com/drive/v3/about?fields=exportFormats`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      const exportFormats = _.get(response, "data.exportFormats", []);
      const exportMimeTypes = _.get(exportFormats, file.mimeType, []);

      if (!_.isEmpty(exportMimeTypes)) {
        const fileMimeType = _.head(
          _.intersection(
            exportMimeTypes,
            _.get(GOOGLE_EXPORT_FORMATS, file.mimeType, [])
          )
        );

        // Check if File format exists
        if (!_.isEmpty(fileMimeType)) {
          let exportUrl = `${baseUrl}/export?mimeType=${fileMimeType}&key=${GOOGLE_DEVELOPER_KEY}&supportsAllDrives=true`;
          try {
            // Make a GET request to Drive API to retrieve export links for the file
            const response = await axios.get(
              `https://www.googleapis.com/drive/v3/files/${file.id}?fields=exportLinks&supportsAllDrives=true`,
              {
                headers: { Authorization: `Bearer ${accessToken}` },
              }
            );

            // Extract export links from the API response; default to an empty object if not present
            const exportLinks = _.get(response, "data.exportLinks", {});

            // Update the export URL with the specific link for the MIME type if available
            exportUrl = _.get(exportLinks, fileMimeType, exportUrl);
          } catch (e) {
            console.error(e);
          }

          exportLinkObjects.push({
            label: "common:drive_share_as_file",
            subText: "common:drive_share_as_file_subtext",
            value: "FILE",
            url: exportUrl,
            mimeType: fileMimeType,
          });
        }
      } else {
        // This gets executed when a file doesn't belong to google document
        exportLinkObjects.push({
          label: "common:drive_share_as_file",
          subText: "common:drive_share_as_file_subtext",
          value: "FILE",
          url: `${baseUrl}?alt=media&supportsAllDrives=true`,
          mimeType: file.mimeType,
        });
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  } else if (serviceType === "MICROSOFT") {
    exportLinkObjects.push({
      label: "common:drive_share_as_file",
      subText: "common:drive_share_as_file_subtext",
      value: "FILE",
      url: file.downloadUrl,
      mimeType: file.mimeType,
    });
  }
  if (isShareAsFileFirst) {
    exportLinkObjects.push(linkObj);
  }

  return exportLinkObjects;
};

/**
 * This function returns a single accessible url for a chosen file from
 * google picker.
 *
 * @param {Object} param0
 * @param {Object} param0.file - Chosen file object from google picker
 * @param {String} param0.accessToken - Google access token
 *
 * @return {String} - File url
 */
export const getExportableFileUrl = async ({ file, accessToken, userType }) => {
  const exportLinks = await getExportLinks({ file, accessToken, userType });
  return _.get(_.find(exportLinks, { value: "FILE" }, {}), "url");
};

const getRethumb = ({ imageUrl, width, height, fitIn = true }) => {
  const rethumbUrl = imageUrl
    ? getRethumbUrl({
        width: width,
        height: height,
        imageUrl: imageUrl,
        fitIn: fitIn,
      })
    : "";
  return `${rethumbUrl}`;
};

export const getFileType = mimetype => {
  const fileType = mimetype ? mimetype.split("/")[0] : "";

  if (fileType == "image") {
    return { text: "IMAGE", locale: "common:image" };
  } else if (fileType == "video") {
    return { text: "VIDEO", locale: "common:video" };
  } else if (fileType == "audio") {
    return { text: "AUDIO", locale: "common:audio" };
  } else {
    return { text: "FILE", locale: "common:file" };
  }
};

export const MIME_TYPE_MAPPER = {
  "application/pdf": "PDF",
  "application/zip": "Zip",
  "application/vnd.google-apps.document": "Google Doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "Word Document",
  "application/msword": "MS Word",
  "application/x-iwork-pages-sffpages": "Apple iWork Pages",
  "application/vnd.apple.pages": "Apple Pages",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "Spreadsheet",
  "application/vnd.google-apps.spreadsheet": "Google Sheet",
  "application/vnd.ms-excel": "MS Excel",
  "application/x-iwork-numbers-sffnumbers": "Apple iWork Numbers",
  "application/vnd.apple.numbers": "Apple Numbers",
  "text/csv": "CSV",
  "application/vnd.google-apps.presentation": "Google Slide",
  "application/vnd.ms-powerpoint": "MS Powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "Presentation",
  "application/x-iwork-keynote-sffkey": "Apple iWork Keynote",
  "application/vnd.apple.keynote": "Apple Keynote",
  "text/xml": "XML",
  "text/plain": "Text File",
};

export const getMediaCardFileType = ({ type, mimeType }) => {
  if (type === "TODDLE DOC") return "Toddle Document";
  if (type === "CODE_EDITOR") return "Code editor";
  if (type !== "FILE") return stringToPascalCase({ string: type ?? "" });
  else {
    const fileType = MIME_TYPE_MAPPER[mimeType];
    if (fileType) return fileType;
    else return "Unknown File";
  }
};

export const isLocalUrl = url => {
  return _.includes(url, "blob");
};

export const getGoogleFileType = mimeType => {
  switch (mimeType) {
    case "application/vnd.google-apps.document":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/msword":
    case "application/x-iwork-pages-sffpages":
    case "application/vnd.apple.pages":
      return "DOC";
    case "application/epub+zip":
      return "EPUB";
    case "application/pdf":
      return "PDF";
    case "application/vnd.google-apps.spreadsheet":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.ms-excel":
    case "application/x-iwork-numbers-sffnumbers":
    case "application/vnd.apple.numbers":
      return "SHEET";
    case "application/vnd.google-apps.presentation":
    case "application/vnd.ms-powerpoint":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/x-iwork-keynote-sffkey":
    case "application/vnd.apple.keynote":
      return "SLIDES";
    case "application/vnd.google-apps.drawing":
      return "DRAWING";
    default:
      return null;
  }
};

export const getFileThumbUrl = mimeType => {
  let thumbUrl = "";
  let color = `${colors.white}`;
  let borderColor = `${colors.strokeTwo}`;
  let fileType = "";

  switch (mimeType) {
    case "application/vnd.google-apps.document":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/msword":
    case "application/x-iwork-pages-sffpages":
    case "application/vnd.apple.pages":
      thumbUrl = PICKER_DEFAULT_IMAGES[`DOCS`];
      color = "#ecf2fd";
      borderColor = "#2b83fa";
      fileType = "DOCUMENT";
      break;
    case "application/epub+zip":
      thumbUrl = PICKER_DEFAULT_IMAGES[`EPUB`];
      color = "#F4F8F9";
      borderColor = "#20475A";
      fileType = "ZIP";
      break;
    case "application/pdf":
      thumbUrl = PICKER_DEFAULT_IMAGES[`PDF`];
      color = "#fceceb";
      borderColor = "#ea4336";
      fileType = "PDF";
      break;
    case "video/webm;codecs=vp8,opus":
    case "video/webm":
    case "video/mp4":
    case "video/avi":
    case "video/wmv":
    case "video/quicktime":
    case "video/mpeg":
    case "video/x-matroska":
      thumbUrl = PICKER_DEFAULT_IMAGES[`VIDEO`];
      color = "#6464dc";
      borderColor = "#6464dc";
      fileType = "VIDEO";
      break;
    case "application/vnd.google-apps.spreadsheet":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.ms-excel":
    case "application/x-iwork-numbers-sffnumbers":
    case "application/vnd.apple.numbers":
    case "text/csv":
      thumbUrl = PICKER_DEFAULT_IMAGES[`SHEET`];
      color = "#e7f5ee";
      borderColor = "#0c9d59";
      fileType = "SHEET";
      break;
    case "application/vnd.ms-powerpoint.presentation.macroenabled.12":
    case "application/vnd.google-apps.presentation":
    case "application/vnd.ms-powerpoint":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/x-iwork-keynote-sffkey":
    case "application/vnd.apple.keynote":
      thumbUrl = PICKER_DEFAULT_IMAGES[`SLIDES`];
      color = "#fef7e8";
      borderColor = "#f9b615";
      fileType = "SLIDES";
      break;
    case "application/zip":
      thumbUrl = PICKER_DEFAULT_IMAGES[`ZIP`];
      color = "#fef7e8";
      borderColor = "#f9b615";
      fileType = "ZIP";
      break;
    case "application/vnd.google-apps.drawing":
      thumbUrl = PICKER_DEFAULT_IMAGES["DRAWING"];
      color = "#FBEDEC";
      fileType = "DRAWING";
      break;
    case "audio/mpeg":
    case "audio/mp3":
    case "audio/mp4":
    case "audio/wav":
    case "audio/vnd.wav":
    case "audio/x-m4a":
    case "audio/aac":
    case "audio/wave":
    case "audio/vnd.wave":
    case "audio/x-wav":
      thumbUrl = PICKER_DEFAULT_IMAGES["AUDIO"];
      color = "#EFEFFB";
      fileType = "AUDIO";
      break;

    case "image/jpeg":
    case "image/jpg": // some image files uploaded from mobile device have mimeType image/jpg having extention .jpg in file.
    case "image/png":
    case "image/gif":
    case "image/bmp":
    case "image/svg+xml":
    case "image/tiff":
    case "image/webp":
    case "image/heic":
    case "image/heif":
    case "image/svg":
      thumbUrl = PICKER_DEFAULT_IMAGES["IMAGE"];
      color = "#E8EDF0";
      fileType = "IMAGE";
      break;

    case "text/xml":
      thumbUrl = PICKER_DEFAULT_IMAGES["XML"];
      fileType = "XML";
      break;

    default:
      thumbUrl = PICKER_DEFAULT_IMAGES[`UNKNOWN`];
      color = "#f8f8f8";
      fileType = "UNKNOWN";
      break;
  }

  return { thumbUrl, color, borderColor, fileType };
};

export const getThumbUrl = ({
  attachment,
  width = 256,
  height = 256,
  fitIn = true,
  onlyIcon = false,
  cardType = "horizontal",
}) => {
  if (!attachment) {
    return {};
  }

  const { type, mimeType, url, thumbUrl } = attachment;

  let newThumbUrl = "";
  let isIcon = false;
  let color = `${colors.white}`;
  let borderColor = `${colors.strokeTwo}`;
  let fileType = "UNKNOWN";

  if (onlyIcon) {
    isIcon = true;
    const {
      thumbUrl: iconThumbUrl,
      color: iconColor,
      borderColor: borderColorAlias,
      fileType: attachmentFileType,
    } = getFileThumbUrl(mimeType);
    color = iconColor;
    newThumbUrl = iconThumbUrl;
    borderColor = borderColorAlias;
    fileType = attachmentFileType;
    let fileExtension = getFileExtensionFromMimeType({ mimeType });
    if (fileExtension == false) fileExtension = "unknown";
    return {
      thumbUrl: newThumbUrl,
      color,
      isIcon: isIcon,
      borderColor,
      fileType,
      fileExtension:
        fileExtension == "unknown" ? fileExtension : `.${fileExtension}`,
    };
  }
  if (type == "NOTE") {
    newThumbUrl = PICKER_DEFAULT_IMAGES["NOTE"];
    isIcon = true;
    fileType = "NOTE";
  } else if (type === "TODDLE_DOC") {
    isIcon = true;
    fileType = "TODDLE_DOC";
  } else if (type == "IMAGE") {
    newThumbUrl = isLocalUrl(url)
      ? `${url}`
      : getRethumb({ imageUrl: url, width, height, fitIn });
  } else if (type === "WORKBOOK") {
    newThumbUrl =
      cardType === "horizontal"
        ? PICKER_DEFAULT_IMAGES["WORKBOOK"]
        : _.get(attachment, "thumbUrl", "");
    isIcon = cardType === "horizontal";
    color = colors.pink950;
    borderColor = colors.strokeTwo;
    fileType = "WORKBOOK";
  } else if (thumbUrl) {
    newThumbUrl =
      mimeType == "application/pdf"
        ? thumbUrl
        : getRethumb({
            imageUrl: thumbUrl,
            width,
            height,
            fitIn,
          });
  } else if (type == "EQ") {
    newThumbUrl = PICKER_DEFAULT_IMAGES["MATH_EQ"];
    isIcon = true;
    color = colors.neutral800;
  } else if (type == "QUESTION" || type == "QUESTION_SUBMISSION") {
    isIcon = true;
    fileType = "WORKSHEET";
  } else if (type == "IMAGE_0") {
    newThumbUrl = PICKER_DEFAULT_IMAGES[type];
  } else if (type == "IMAGE_1") {
    newThumbUrl = PICKER_DEFAULT_IMAGES[type];
  } else if (type == "IMAGE_2") {
    newThumbUrl = PICKER_DEFAULT_IMAGES[type];
  } else if (type == "IMAGE_3") {
    newThumbUrl = PICKER_DEFAULT_IMAGES[type];
  } else if (type == "IMAGE_4") {
    newThumbUrl = PICKER_DEFAULT_IMAGES[type];
  } else if (type == "IMAGE_5") {
    newThumbUrl = PICKER_DEFAULT_IMAGES[type];
  } else if (type == "IMAGE_6") {
    newThumbUrl = PICKER_DEFAULT_IMAGES[type];
  } else if (type == "IMAGE_7") {
    newThumbUrl = PICKER_DEFAULT_IMAGES[type];
  } else {
    isIcon = true;
    const {
      thumbUrl: iconThumbUrl,
      color: iconColor,
      borderColor: borderColorAlias,
      fileType: attachmentFileType,
    } = getFileThumbUrl(mimeType);
    color = iconColor;
    newThumbUrl = iconThumbUrl;
    borderColor = borderColorAlias;
    fileType = attachmentFileType;
  }
  let fileExtension = getFileExtensionFromMimeType({ mimeType });
  if (fileExtension == false) fileExtension = "unknown";

  return {
    thumbUrl: newThumbUrl,
    color,
    isIcon: isIcon,
    borderColor,
    fileType: fileType,
    fileExtension:
      fileExtension == "unknown" ? fileExtension : `.${fileExtension}`,
  };
};

const ICON_MAPPER_DISABLED = {
  IMAGE: <ImageSquareOutlined variant="subtle" />,
  AUDIO: <VoiceWaveOutlined variant="subtle" />,
  VIDEO: <VideoOutlined variant="subtle" />,
  WORKBOOK: <WorkbookOutlined variant="subtle" />,
  NOTE: <NoteOutlined variant="subtle" />,
  POST: <PostOneOutlined variant="subtle" />,
  FOLDER: <FolderOutlined variant="subtle" />,
  RUBRIC: <RubricOutlined variant="subtle" />,
  "SINGLE POINT RUBRIC": <SinglePointRubricOutlined variant="subtle" />,
  "MYP CRITERIA BASED RUBRIC": <SinglePointRubricOutlined variant="subtle" />,
  "DP ASSESSMENT RUBRIC": <TableOutlined variant="subtle" />,
  CHECKLIST: <ChecklistOutlined variant="subtle" />,
  "LEARNING GOALS": <GoalsOutlined variant="subtle" />,
  "SCORE BASED ASSESSMENT": <ScoreBasedAssessmentOutlined variant="subtle" />,
  "PROGRESS SET": <ProgressReportOutlined variant="subtle" />,
  COMMENT: <CommentOutlined variant="subtle" />,
  QUESTION: <WorksheetClipboardOutlined variant="subtle" />,
  QUESTION_SUBMISSION: <WorksheetClipboardOutlined variant="subtle" />,
  CODE_EDITOR: <CodeOutlined variant="subtle" />,
};

const ICON_MAPPER = {
  IMAGE: <ImageColored />,
  AUDIO: <AudioColored />,
  VIDEO: <VideoColored />,
  WORKBOOK: <WorkbookColored />,
  NOTE: <NoteColored />,
  POST: <PostOneOutlined className="text-interactiveThreeDefault" />,
  FOLDER: <FolderColored />,
  RUBRIC: <RubricOutlined />,
  "SINGLE POINT RUBRIC": <SinglePointRubricOutlined />,
  "MYP CRITERIA BASED RUBRIC": <SinglePointRubricOutlined />,
  "DP ASSESSMENT RUBRIC": <TableOutlined />,
  CHECKLIST: <ChecklistOutlined />,
  "LEARNING GOALS": <GoalsOutlined variant={"success"} />,
  "SCORE BASED ASSESSMENT": <ScoreBasedAssessmentOutlined />,
  "PROGRESS SET": <ProgressReportOutlined />,
  COMMENT: <CommentTextOutlined />,
  QUESTION: <WorksheetClipboardOutlined />,
  QUESTION_SUBMISSION: <WorksheetClipboardOutlined />,
  CODE_EDITOR: (
    <CodeOutlined className="!text-decorative-foreground-subtle-violet" />
  ),
};

const ICON_MIME_MAPPER_DISABLED = {
  LINK: <LinkOutlined variant="subtle" />,
  PDF: <PdfOutlined variant="subtle" />,
  DOC: <PageSquareOutlined variant="subtle" />,
  PPT: <SlidesOutlined variant="subtle" />,
  XML: <CodeOutlined variant="subtle" />,
  TEXT: <HamburgerOutlined variant="subtle" />,
  SPREADSHEET: <SheetsOutlined variant="subtle" />,
  UNKNOWN_FILE: <FileNotFoundOutlined variant="subtle" />,
  ZIP: <ZipFileOutlined variant="subtle" />,
};

const ICON_MIME_MAPPER = {
  LINK: <LinkColored />,
  PDF: <PdfColored />,
  DOC: <PageSquareColored />,
  PPT: <SlidesColored />,
  XML: <CodeOutlined variant="subtle" />,
  TEXT: <HamburgerOutlined variant="subtle" />,
  SPREADSHEET: <SheetsColored />,
  UNKNOWN_FILE: <FilenotfoundColored />,
  ZIP: <ZipFileColored />,
};

export const getThumbnailIcon = ({ type, mimeType, disabled = false }) => {
  const icon = disabled ? ICON_MAPPER_DISABLED[type] : ICON_MAPPER[type];
  if (icon) return icon;
  else if (type === "LINK" && mimeType == null) {
    return disabled ? ICON_MIME_MAPPER_DISABLED.LINK : ICON_MIME_MAPPER.LINK;
  } else {
    switch (mimeType) {
      case "application/pdf":
        return disabled ? ICON_MIME_MAPPER_DISABLED.PDF : ICON_MIME_MAPPER.PDF;
      case "application/zip":
        return disabled ? ICON_MIME_MAPPER_DISABLED.ZIP : ICON_MIME_MAPPER.ZIP;
      case "application/vnd.google-apps.document":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "application/msword":
      case "application/x-iwork-pages-sffpages":
      case "application/vnd.apple.pages":
        return disabled ? ICON_MIME_MAPPER_DISABLED.DOC : ICON_MIME_MAPPER.DOC;
      case "audio/mpeg":
      case "audio/mp3":
      case "audio/mp4":
      case "audio/x-m4a":
      case "audio/wav":
      case "audio/vnd.wav":
      case "audio/aac":
      case "audio/wave":
      case "audio/vnd.wave":
      case "audio/x-wav":
        return disabled ? ICON_MAPPER_DISABLED.AUDIO : ICON_MAPPER.AUDIO;
      case "video/webm;codecs=vp8,opus":
      case "video/webm":
      case "video/mp4":
      case "video/avi":
      case "video/wmv":
      case "video/quicktime":
      case "video/mpeg":
      case "video/x-matroska":
        return disabled ? ICON_MAPPER_DISABLED.VIDEO : ICON_MAPPER.VIDEO;
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      case "application/vnd.google-apps.spreadsheet":
      case "application/vnd.ms-excel":
      case "application/x-iwork-numbers-sffnumbers":
      case "application/vnd.apple.numbers":
      case "text/csv":
        return disabled
          ? ICON_MIME_MAPPER_DISABLED.SPREADSHEET
          : ICON_MIME_MAPPER.SPREADSHEET;
      case "application/vnd.google-apps.presentation":
      case "application/vnd.ms-powerpoint":
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      case "application/x-iwork-keynote-sffkey":
      case "application/vnd.apple.keynote":
        return disabled ? ICON_MIME_MAPPER_DISABLED.PPT : ICON_MIME_MAPPER.PPT;
      case "text/plain":
        return disabled
          ? ICON_MIME_MAPPER_DISABLED.TEXT
          : ICON_MIME_MAPPER.TEXT;
      case "text/xml":
        return disabled ? ICON_MIME_MAPPER_DISABLED.XML : ICON_MIME_MAPPER.XML;
      default:
        return disabled
          ? ICON_MIME_MAPPER_DISABLED.UNKNOWN_FILE
          : ICON_MIME_MAPPER.UNKNOWN_FILE;
    }
  }
};

export const getFileName = (fileNameWithExtension, mimeType) => {
  if (fileNameWithExtension) {
    if (!_.isEmpty(mimeType)) {
      if (
        getFileExtension(fileNameWithExtension) === mime.extension(mimeType) &&
        !_.isEqual(mime.extension(mimeType), false)
      ) {
        return fileNameWithExtension.replace(/\.[^/.]+$/, "");
      } else {
        return fileNameWithExtension;
      }
    } else {
      //No mimeType - uploaded from device, so remove the string after last ".".
      return fileNameWithExtension.replace(/\.[^/.]+$/, "");
    }
  } else {
    return "";
  }
};

export const getImageDimensions = async ({ url, type }) => {
  let result = { height: 520, width: 520 };
  try {
    result = await new Promise((resolve, reject) => {
      if (type == "VIDEO") {
        //console.log(url, type);
        const video = document.createElement("video");
        if (video) {
          //Safer side
          video.setAttribute("src", url);
          //video.src = url;
          video.onloadedmetadata = event => {
            const { videoHeight, videoWidth } = _.get(event, "srcElement", {});

            resolve({ height: videoHeight, width: videoWidth });
          };

          //Remove unnecessary event
          // video.onloadeddata = event => {
          //   const { videoHeight, videoWidth } = _.get(event, "srcElement", {});
          //   resolve({ height: videoHeight, width: videoWidth });
          // };
          video.onerror = event => {
            reject();
          };
          // video.addEventListener("error", event => {
          //   console.log("error");
          //   resolve({});
          // });
        } else {
          reject();
        }
      } else if (type == "IMAGE") {
        const img = new Image();
        img.src = url;
        img.onload = () => resolve({ height: img.height, width: img.width });
        img.onerror = () => {
          reject();
        };
      } else {
        reject();
      }
    });
  } catch (e) {}
  return result;
};

export const getFileExtension = fileName => {
  return fileName ? fileName.substring(fileName.lastIndexOf(".") + 1) : "";
};

export const downloadFileFromUrl = file => {
  const xhr = new XMLHttpRequest();
  xhr.withCredentials = allowURLWithCredentials(file.url);
  xhr.open("GET", file.url, true);
  xhr.responseType = "blob";

  xhr.onload = function (e) {
    if (this.status == 200) {
      var blob = this.response;
      saveAs(blob, file.name);
    }
  };
  xhr.send();
};

export const formatBytes = ({ bytes, decimals = 2 }) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const isImageCarousel = ({ attachments }) => {
  return (
    _.get(attachments, "length", 0) > 1 &&
    !_.find(attachments, item => _.get(item, "type", "") != "IMAGE")
  );
};

export const isAudioFile = ({ attachments }) => {
  return (
    _.get(attachments, "length", 0) == 1 &&
    _.get(attachments[0], "type") == "AUDIO"
  );
};

export const hasAudioNote = ({ attachments }) => {
  return (
    _.get(attachments, "length", 0) > 1 &&
    _.get(attachments[1], "type") == "AUDIO"
  );
};

export const getAttachmentTypes = ({ attachments }) => {
  let mediaType =
    POST_MEDIA_TYPES[attachments.length > 0 ? attachments[0].type : ""];
  if (isImageCarousel({ attachments })) {
    mediaType = POST_MEDIA_TYPES["MULTI_IMAGE"];
  }
  return mediaType;
};

export const isAllImages = ({ attachments }) => {
  return !_.find(attachments, item => _.get(item, "type", "") != "IMAGE");
};

export const getUpdatedMaxSize = ({ maxSize, fileType }) => {
  const updatedMaxSize = _.isArray(maxSize)
    ? _.get(
        _.find(maxSize, item => _.includes(item.accept, fileType)),
        "fileTypeMaxSize",
        0
      ) || _.max(_.map(maxSize, "fileTypeMaxSize"))
    : maxSize;

  return updatedMaxSize;
};

export const getFileTypesAndMaxSizes = ({
  files,
  allowedFiles,
  fileTypePropName = "mimeType",
  maxSize,
  maxSizeForVideoFiles,
  maxSizeForZipFiles,
  is500MBVideoLimitEnabled = false,
  isIncreasedUploadLimit = false,
}) => {
  const filesAboveMaxSizeLimit = _.difference(files, allowedFiles);

  const fileTypesAndMaxSizes = _.map(filesAboveMaxSizeLimit, file => {
    const fileTypeValue =
      _.get(file, fileTypePropName) ||
      getMimeTypeFromFileName({ fileName: _.get(file, "name") });
    let maxSizeForFile = maxSize;
    if (
      (is500MBVideoLimitEnabled &&
        !_.isArray(maxSize) &&
        _.startsWith(_.get(file, fileTypePropName), "video/")) ||
      _.includes(
        getMimeTypeFromFileName({ fileName: _.get(file, "name", "") }),
        "video"
      )
    ) {
      maxSizeForFile = maxSizeForVideoFiles;
    }

    if (
      isIncreasedUploadLimit &&
      !_.isArray(maxSize) &&
      _.includes(
        ARCHIVE_MIME_TYPES,
        getMimeTypeFromFileName({ fileName: _.get(file, "name", "") })
      )
    ) {
      maxSizeForFile = maxSizeForZipFiles;
    }

    const updatedMaxSize = getUpdatedMaxSize({
      maxSize: maxSizeForFile,
      fileType: fileTypeValue,
    });

    return {
      fileMaxSize: updatedMaxSize,
      fileType: fileTypeValue,
    };
  });

  return fileTypesAndMaxSizes;
};

export const getFileTypesAndMaxSizesV2 = ({
  files,
  allowedFiles,
  maxSize,
  areFilesInRawFormat = false,
}) => {
  const filesAboveMaxSizeLimit = _.difference(files, allowedFiles);
  const fileTypesAndMaxSizes = _.map(filesAboveMaxSizeLimit, file => {
    let name,
      type,
      mimeType,
      size,
      resourceMapId = null;

    if (areFilesInRawFormat) {
      // fetch the type, mimetype, name and size of the file
      name = _.get(file, "name");
      mimeType = file.type;
      if (!mimeType) {
        mimeType = getMimeTypeFromFileName({ fileName: name });
      }
      type = getFileType(mimeType).text;
      size = _.get(file, "size", 0);
    } else {
      // fetch all details directly from the file object
      name = _.get(file, "attachment.label");
      mimeType = _.get(file, "attachment.mimeType");
      type = _.get(file, "attachment.type");
      resourceMapId = _.get(file, "resourceMapId", "");
      size = _.get(file, "attachment.metadata.size", 0);
    }

    const updatedMaxSize = getUpdatedMaxSize({
      maxSize,
      fileType: mimeType,
    });

    return {
      name,
      type,
      mimeType,
      size,
      resourceMapId,
      allowedSize: updatedMaxSize,
    };
  });

  return fileTypesAndMaxSizes;
};

export const getDragNDropAttachmentSendObj = async (file, item) => {
  let mimeType = file.type;

  if (!mimeType) {
    mimeType = getMimeTypeFromFileName({ fileName: file.name });
  }

  const attachmentType = _.get(getFileType(mimeType), "text", "");
  const url = item;

  const dimensions = await getImageDimensions({
    url,
    type: attachmentType,
  });

  const getWebkitRelativePath = file => {
    const { path = "", webkitRelativePath = "" } = file;
    if (path && path.split("/").length > 1 && path.startsWith("/")) {
      // it is a folder
      return path.slice(1);
    } else {
      return path;
    }
  };

  const sendObj = {
    url: url,
    name: getFileName(file.name),
    mimeType,
    type: attachmentType,
    metadata: {
      size: _.get(file, "size", 0),
      fileExtension: getFileExtensionFromFilename({ fileName: file.name }),
      webkitRelativePath: getWebkitRelativePath(file),
      ...dimensions,
    },
  };

  return sendObj;
};

export const buildStructure = fileArray => {
  const structure = [];
  const fileMapping = {};

  function addToStructure({
    currentPath: path,
    resourceType,
    currentParentId: parentId = null,
    fileOrFolder: fileData,
  }) {
    const entry = {
      resourceMapId: generateRandomIdV2({ characters: "123456789" }),
      label: path.split("/").pop(),
      resourceType: resourceType,
      parentResourceMapId: parentId,
    };
    if (resourceType === "FILE") {
      fileMapping[entry.resourceMapId] = fileData.file;
      delete fileData.file;
      entry.attachment = JSON.parse(JSON.stringify(fileData || {}));
    }
    return entry;
  }

  for (const fileOrFolder of fileArray) {
    const webkitRelativePath = _.get(
      fileOrFolder,
      "metadata.webkitRelativePath",
      ""
    );

    const parts = webkitRelativePath.split("/");

    let currentParentId = null;

    for (let i = 0; i < parts.length; i++) {
      const currentPath = parts.slice(0, i + 1).join("/");
      const isFile = i === parts.length - 1;
      const existingEntry = structure.find(
        entry =>
          entry.label === parts[i] &&
          entry.parentResourceMapId === currentParentId
      );

      if (!existingEntry) {
        const entry = addToStructure({
          currentPath,
          resourceType: isFile ? "FILE" : "FOLDER",
          currentParentId,
          fileOrFolder,
        });

        // Set parentResourceMapId of the root folder to null
        if (i === 0) {
          entry.parentResourceMapId = null;
        }

        structure.push(entry);
        currentParentId = entry.resourceMapId;
      } else {
        currentParentId = existingEntry.resourceMapId;
      }
    }
  }
  const jsonResourceObj = structure.map(resource => {
    if (resource.resourceType === "FILE") {
      const clonedFileObj = _.cloneDeep(resource);
      if (clonedFileObj.attachment) {
        delete clonedFileObj.attachment.metadata.webkitRelativePath;
      }
      return {
        ...clonedFileObj,
        // remove file extension from label
        label: clonedFileObj.label.replace(/\.[^/.]+$/, ""),
      };
    }
    return resource;
  });
  return { jsonResourceObj, fileMapping };
};

export const getAttachmentSendObj = async (file, item) => {
  let mimeType = file.type;

  if (!mimeType) {
    mimeType = getMimeTypeFromFileName({ fileName: file.name });
  }

  const attachmentType = getFileType(mimeType).text;
  const url = item;

  const dimensions = await getImageDimensions({
    url,
    type: attachmentType,
  });

  const sendObj = {
    url: url,
    name: getFileName(file.name),
    mimeType,
    type: attachmentType,
    metadata: {
      duration: _.get(file, "duration"),
      size: _.get(file, "size", 0),
      fileExtension: getFileExtensionFromFilename({ fileName: file.name }),
      webkitRelativePath:
        _.get(file, "path", "") || _.get(file, "webkitRelativePath", ""),
      ...dimensions,
    },
  };

  return sendObj;
};

/**
 * This function returns file extension from file name
 * if filename doesn't contain extension then it will return undefined
 * Read more : https://stackoverflow.com/a/680982
 */
export const getFileExtensionFromFilename = ({ fileName }) => {
  const regex = /(?:\.([^.]+))?$/;
  return regex.exec(fileName)[1];
};

export const getFileExtensionFromMimeType = ({ mimeType }) => {
  if (mimeType == "image/jpg") return "jpg";
  else if (mimeType == "audio/aac") return "aac";
  else if (mimeType == "video/avi") return "avi";
  else if (mimeType == "video/wmv") return "wmv";
  else return mime.extension(mimeType);
};

export const getMimeTypeFromFileName = ({ fileName }) => {
  const fileExtension = getFileExtension(fileName);

  return (
    mime.lookup(fileName) || MISC_FILE_EXTENSION_MIMETYPE_MAPPING[fileExtension]
  );
};

export const getDriveShareTemplateOptions = ({
  file,
  serviceType = "GOOGLE",
}) => {
  const baseUrl = _.isEqual(serviceType, "MICROSOFT")
    ? file.url
    : `https://www.googleapis.com/drive/v3/files/${file.id}`;

  const { text: type } = getFileType(file.mimeType) || {};
  if (
    type == "IMAGE" ||
    type == "VIDEO" ||
    type == "AUDIO" ||
    file.mimeType == "application/pdf"
  ) {
    return [
      {
        ...(_.find(TEMPLATE_OPTIONS, item => item.value == "copy") || {}),
        exportAs: "FILE",
        url: `${baseUrl}?alt=media&supportsAllDrives=true`,
        mimeType: file.mimeType,
      },
    ];
  } else {
    return _.map(TEMPLATE_OPTIONS, item => {
      return {
        ...(item || {}),
        url: file.url,
        exportAs: "LINK",
        mimeType: file.mimeType,
      };
    });
  }
};

export const getCompressedBlob = async ({ chunks, mimeType }) => {
  try {
    return {
      blob: new Blob(chunks, { type: mimeType }),
      mimeType,
    };

    // // //file name
    // const name = "video.webm";

    // console.log("filename", name);

    // const mediaData = new Uint8Array(await new Blob(chunks).arrayBuffer());
    // console.log("ffmpeg", mediaData);
    // if (!ffmpeg.isLoaded()) {
    //   await ffmpeg.load();
    // }
    // ffmpeg.FS("writeFile", name, await fetchFile(mediaData));
    // await ffmpeg.run("-i", name, "output.mp4");
    // const data = ffmpeg.FS("readFile", "output.mp4");
    // // console.log("file", blob);
    // return { blob: new Blob([data.buffer]), mimeType: "video/mp4" };
  } catch (e) {
    console.error("e", e);
    return {
      blob: new Blob(chunks, { type: mimeType }),
      mimeType,
    };
  }
};

export const getOrgResourcesAttachments = ({ resources }) => {
  return _.reduce(
    resources,
    (result, resource) => {
      const resourceToAdd = _.get(resource, "item", null)
        ? _.get(resource, "item.attachment", null)
        : _.get(resource, "attachment", null);

      if (resourceToAdd) {
        const name = resource.label || resourceToAdd.name;
        result.push({ ...resourceToAdd, name });
      }
      return result;
    },
    []
  );
};

export const getAttachmentCardTypeForAttachmentList = ({
  isClubbing,
  index,
  cardType,
  clubbingCards,
  showClubbedCard,
  showInlineAudioPlayer = false,
}) => {
  if (isClubbing && !showClubbedCard && index >= clubbingCards - 1) {
    /* case when the clubbing is enabled and
      "see more" button is not clicked
    */
    if (index === clubbingCards - 1) return "ClubbingCard";
    else return "null";
  } else {
    /* All the other cases
     ** when the "see more" button is clicked
     ** when clubbing is disabled
     ** when index < clubbingCards - 1
     */
    if (showInlineAudioPlayer) {
      return "horizontalV2";
    }
    return cardType;
  }
};

export const getAttachmentIdForUploadedAttachment = ({ attachment }) => {
  // returns correct uploaded id of the attachment
  if (
    attachment &&
    "attachmentId" in attachment &&
    !_.startsWith(_.get(attachment, "attachmentId"), "NEW")
  ) {
    return _.get(attachment, "attachmentId");
  } else {
    return _.get(attachment, "id");
  }
};

export const getAttachmentByTypeFromAttachmentList = ({
  attachmentList,
  type,
}) => {
  // returns attachment of the required type if present, otherwise null
  const filteredAttachments = _.filter(
    attachmentList,
    attachment =>
      _.get(_.first(_.get(attachment, "attachments")), "type", "") === type
  );
  if (_.isEmpty(filteredAttachments)) {
    return null;
  }

  return _.first(_.get(_.first(filteredAttachments), "attachments", {}));
};

export const getAttachmentsByTypeFromAttachmentArr = ({
  attachmentsArr,
  type,
  except = false,
}) => {
  let filteredAttachments = _.filter(attachmentsArr, attachment =>
    _.isEqual(_.get(attachment, "type", ""), type)
  );
  if (except) {
    filteredAttachments = _.filter(
      attachmentsArr,
      attachment => !_.isEqual(_.get(attachment, "type", ""), type)
    );
  }
  return filteredAttachments;
};

export const readableBytes = bytes => {
  if (typeof bytes !== "number") return "-";
  if (bytes == 0) return 0;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return (bytes / Math.pow(1024, i)).toFixed(2) * 1 + " " + sizes[i];
};

export const getGoogleFileDetails = async ({ accessToken, fileId }) => {
  try {
    const response = await axios.get(
      `https://www.googleapis.com/drive/v3/files/${fileId}?fields=thumbnailLink&supportsAllDrives=true`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    return response;
  } catch (e) {
    return { thumbnailLink: DEFAULT_THUMB_IMAGE };
  }
};

export const getMicrosoftFileDetails = async ({ accessToken, fileId }) => {
  const size = 0;
  const thumbnailType = "large";
  try {
    // https://graph.microsoft.com/v1.0/me/drive/items/root/children?%24top=30&%24expand=thumbnails

    const response = await axios.get(
      `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/thumbnails/${size}/${thumbnailType}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    return {
      thumbnailLink: _.get(response, "data.url", DEFAULT_THUMB_IMAGE),
    };
  } catch (e) {
    return { thumbnailLink: DEFAULT_THUMB_IMAGE };
  }
};

export const getSharepointFileDetails = async ({
  accessToken,
  driveId,
  fileId,
}) => {
  const size = 0;
  const thumbnailType = "large";
  //if driveId is empty trying to find item in default drive
  const apiEndPoint = _.isEmpty(driveId)
    ? `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/thumbnails/${size}/${thumbnailType}`
    : `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${fileId}/thumbnails/${size}/${thumbnailType}`;
  try {
    const response = await axios.get(apiEndPoint, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return {
      thumbnailLink: _.get(response, "data.url", DEFAULT_THUMB_IMAGE),
    };
  } catch (e) {
    return { thumbnailLink: DEFAULT_THUMB_IMAGE };
  }
};

export const timeHelper = ({ timeInMinutes }) => {
  if (timeInMinutes < 1) {
    return {
      time: Math.round(timeInMinutes * 60),
      locale: "moment:seconds",
    };
  }
  return { time: Math.round(timeInMinutes), locale: "moment:minutes" };
};

export const sizeHelper = ({ size }) => {
  if (size < 100) {
    return size.toFixed(1) + "KB";
  }
  return (size / 1000).toFixed(1) + "MB";
};

export const getTimeLeftInMinutes = ({
  startTime,
  uploadedSize,
  totalSize,
}) => {
  const timeElapsed = new Date() - startTime;
  const uploadSpeed = uploadedSize / (timeElapsed / 1000);
  const timeLeft = uploadSpeed ? (totalSize - uploadedSize) / uploadSpeed : 0;

  return timeLeft / 60;
};

export const getMaxSize = ({
  maxSize,
  isCurriculumProgramFree,
  allowInfiniteFileSize = false,
}) => {
  if (allowInfiniteFileSize) return Infinity;
  return maxSize
    ? maxSize
    : isCurriculumProgramFree
    ? 100 * 1024 * 1024
    : 200 * 1024 * 1024;
};

export const getMaxSizeForVideoFiles = ({
  maxSize,
  isCurriculumProgramFree,
  is500MBVideoLimit,
  allowInfiniteFileSize = false,
}) => {
  if (allowInfiniteFileSize) return Infinity;
  return maxSize
    ? maxSize
    : isCurriculumProgramFree
    ? 100 * 1024 * 1024
    : is500MBVideoLimit
    ? 500 * 1024 * 1024
    : 200 * 1024 * 1024;
};

export const getMaxSizeForZipFiles = ({
  maxSize,
  isCurriculumProgramFree,
  isIncreasedUploadLimit,
  allowInfiniteFileSize = false,
}) => {
  if (allowInfiniteFileSize) return Infinity;
  return maxSize
    ? maxSize
    : isCurriculumProgramFree
    ? 100 * 1024 * 1024
    : isIncreasedUploadLimit
    ? 750 * 1024 * 1024
    : 200 * 1024 * 1024;
};

export const getAttachmentLockingState = ({ attachment }) => {
  const lockingState = _.get(attachment, "lockingState.state", "");

  return lockingState;
};

export const isAttachmentLocked = ({ attachment }) => {
  return getAttachmentLockingState({ attachment }) === ENTITY_LOCKED_STATE;
};

export const getFilteredAttachment = ({ attachment, isLTITool }) => {
  let attachmentObj = attachment;
  const isPost = _.get(attachment, "isPost", false);

  if (isPost || isLTITool) {
    attachmentObj = _.last(_.get(attachment, "attachments", []));
  }

  const name = attachmentObj
    ? attachmentObj.name ?? attachmentObj.title
    : attachment.title;

  attachmentObj = {
    ...attachmentObj,
    id: attachment.id, // post id in case of post
    name,
    ...(isPost && { isPost: true, attachmentId: _.get(attachmentObj, "id") }),
    ...(isLTITool && {
      attachmentId: _.get(attachmentObj, "id"),
    }),
  };

  return attachmentObj;
};

export const checkUserDriveAccess = async ({ accessToken }) => {
  try {
    const userScopeData = await getGoogleScopeOfUser({ accessToken });
    const userScopes = _.split(_.get(userScopeData, "scope", ""), " ");

    if (_.isEmpty(userScopes)) {
      return false;
    }

    const hasDriveScopeV1 = _.every(GOOGLE_DRIVE_SCOPE.SCOPE_V1, scope =>
      _.includes(userScopes, scope)
    );

    const hasDriveScopeV2 = _.every(GOOGLE_DRIVE_SCOPE.SCOPE_V2, scope =>
      _.includes(userScopes, scope)
    );

    return hasDriveScopeV1 || hasDriveScopeV2;
  } catch {
    return false;
  }
};

export const getEntitySubType = ({ projectNodeUID }) => {
  switch (projectNodeUID) {
    case "PP_DETAILS":
      return "PERSONAL_OBJECTIVES";
    case "CP_DETAILS":
      return "COMMUNITY_OBJECTIVES";
    default:
      return "INTERDISCIPLINARY_CRITERIA";
  }
};

export const ARCHIVE_MIME_TYPES = [
  // ZIP
  "application/zip",
  "application/x-zip-compressed",
  "multipart/x-zip",
  // RAR
  "application/vnd.rar",
  "application/x-rar-compressed",
  // TAR
  "application/x-tar",
  // 7Z
  "application/x-7z-compressed",
];

/**
 * Get LTI Tool display name, falling back to raw service name.
 *
 * @param {Object} attachment - Object containing metadata.service.
 * @returns {string} Display name or raw service name of LTI Tool.
 */
export function getLtiToolDisplayName(attachment) {
  const serviceName = _.get(attachment, "metadata.service", "");
  const label = getLTIName(serviceName);
  return label || serviceName;
}

export const checkUserGooglePhotoAccess = async ({ accessToken }) => {
  try {
    const userScopeData = await getGoogleScopeOfUser({ accessToken });
    const userScopes = _.split(_.get(userScopeData, "scope", ""), " ");

    if (_.isEmpty(userScopes)) {
      return false;
    }

    return _.includes(
      userScopes,
      "https://www.googleapis.com/auth/photospicker.mediaitems.readonly"
    );
  } catch {
    return false;
  }
};

export const isMicrosoftLink = attachment => {
  if (_.isEmpty(attachment)) return false;

  const type = _.get(attachment, "type");
  const serviceType =
    _.get(attachment, "metadata.serviceType") ||
    _.get(attachment, "metadata.service");

  return type === "LINK" && serviceType === "MICROSOFT";
};

// Checks if current attachment is a Microsoft link attachment
export const shouldShowSimilarityIndex = attachmentObj => {
  const isCurrentAttachmentMicrosoftLink = isMicrosoftLink(attachmentObj);

  return !isCurrentAttachmentMicrosoftLink;
};

/**
 * Finds an LTI integration credential by service name.
 * For CUSTOM service credentials, matches by `name` instead of `serviceName`.
 *
 * @param {Object} params
 * @param {Array<Object>} params.ltiIntegrationCreds - List of LTI integration credential objects
 * @param {string} params.serviceName - The service name (or custom name) to match against
 * @returns {Object|undefined} The matching LTI credential, or undefined if not found
 */
export const getLTIIntegrationsCred = ({
  ltiIntegrationCreds,
  serviceName,
}) => {
  const ltiIntegrationCred = _.find(ltiIntegrationCreds, cred => {
    if (cred.serviceName === "CUSTOM") {
      return _.isEqual(cred.name, serviceName);
    }
    return _.isEqual(cred.serviceName, serviceName);
  });

  return ltiIntegrationCred;
};

/**
 * Retrieves the viewResponseAfterSubmission flag for an LTI service.
 *
 * @param {Object} params
 * @param {Array<Object>} params.ltiIntegrationCreds - List of LTI integration credential objects
 * @param {string} params.serviceName - The service name to match against
 * @returns {boolean} True if viewResponseAfterSubmission is enabled, false otherwise
 */
export const getViewResponseAfterSubmissionFromServiceName = ({
  ltiIntegrationCreds,
  serviceName,
}) => {
  const ltiIntegrationCred = getLTIIntegrationsCred({
    ltiIntegrationCreds,
    serviceName,
  });

  return _.get(
    ltiIntegrationCred,
    "metadata.viewResponseAfterSubmission",
    false
  );
};
